import {
  BasicPreBookingFragment,
  useGetBusinessHoursLazyQuery,
  useGetPreBookingsLazyQuery,
} from 'drycut-graphql';
import { useEffect, useState } from 'react';
import DaySelect from './DaySelect';
import SlotSelect from './SlotSelect';
import { DateTime } from 'luxon';
import { CircleButton, Heading, Stack } from 'ui-components';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { useCheckoutStore } from '../../checkoutStore';

export type WeekInfo = {
  weekNumber: number;
  year: number;
  weekMonth: string;
  startOfWeek: DateTime;
  endOfWeek: DateTime;
};

type Props = {
  onSlotSelect: (time: DateTime) => void;
};

export function ScheduleSelect({ onSlotSelect }: Props): React.ReactElement {
  const selectedStore = useCheckoutStore((state) => state.selectedStore);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const [selectedDate, setSelectedDate] = useState<DateTime>(selectedSchedule || DateTime.now());

  // The queries to get the relevant data
  const [getBusinessHours, { loading: businessHoursLoading, data: businessHoursData }] =
    useGetBusinessHoursLazyQuery();
  const [getPreBookings, { loading: prebookingsLoading, data: preBookingsData }] =
    useGetPreBookingsLazyQuery();

  const [weekInfo, setWeekInfo] = useState<WeekInfo>({
    weekNumber: DateTime.now().weekNumber,
    year: DateTime.now().year,
    weekMonth: DateTime.now().toFormat('MMMM'),
    startOfWeek: DateTime.now().startOf('week'),
    endOfWeek: DateTime.now().endOf('week'),
  });

  const preBookings = preBookingsData?.preBookings?.nodes || [];
  const validPreBookings = preBookings.filter((node): node is BasicPreBookingFragment =>
    Boolean(node),
  );

  const businessHours = businessHoursData?.businessHours;

  // Fetch the data when the component mounts. We need to refetch the data every time so we are
  // sure we're getting the most up-to-date information.
  useEffect(() => {
    if (selectedStore) {
      getBusinessHours({ variables: { storeId: selectedStore.id }, fetchPolicy: 'network-only' });
      getPreBookings({ variables: { storeId: selectedStore.id }, fetchPolicy: 'network-only' });
    }
  }, [selectedStore, getBusinessHours, getPreBookings]);

  const loading = businessHoursLoading || prebookingsLoading;

  const previousEnabled = !loading && weekInfo.weekNumber !== DateTime.now().weekNumber;

  // Can only navigate to the next week if the current day + 5 days is in the next week.
  const nextEnabled = !loading && DateTime.now().plus({ days: 5 }).weekNumber > weekInfo.weekNumber;

  const handleNextWeek = () => {
    const nextWeekDate = weekInfo.startOfWeek.plus({ weeks: 1 });

    setWeekInfo({
      weekNumber: nextWeekDate.weekNumber,
      year: nextWeekDate.year,
      weekMonth: nextWeekDate.toFormat('MMMM'),
      startOfWeek: nextWeekDate.startOf('week'),
      endOfWeek: nextWeekDate.endOf('week'),
    });
  };

  const handlePreviousWeek = () => {
    const prevWeekDate = weekInfo.startOfWeek.minus({ weeks: 1 });

    setWeekInfo({
      weekNumber: prevWeekDate.weekNumber,
      year: prevWeekDate.year,
      weekMonth: prevWeekDate.toFormat('MMMM'),
      startOfWeek: prevWeekDate.startOf('week'),
      endOfWeek: prevWeekDate.endOf('week'),
    });
  };

  // Final selection, after selecting then time slot, that bubbles up the selection to the context
  const handleSlotSelect = (time: DateTime) => {
    setSelectedDate(time);
    onSlotSelect(time);
  };

  return (
    <Stack gap={6}>
      <Stack gap={3}>
        <Stack direction="row" gap={2} className="justify-between">
          <CircleButton onClick={handlePreviousWeek} disabled={!previousEnabled}>
            <ArrowLeftIcon className="size-6" />
          </CircleButton>

          <Heading variant="h4" className="font-ramillas">
            {weekInfo.startOfWeek.setLocale('de-CH').toFormat('MMMM')}
          </Heading>

          <CircleButton onClick={handleNextWeek} disabled={!nextEnabled}>
            <ArrowRightIcon className="size-6" />
          </CircleButton>
        </Stack>

        {businessHours ? (
          <DaySelect
            weekInfo={weekInfo}
            businessHours={businessHours}
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
          />
        ) : (
          <>Loading</>
        )}
      </Stack>

      <div className="h-px bg-gray-800/5" />

      <Heading variant="h5" className="font-ramillas">
        {selectedDate.setLocale('de-CH').toFormat('EEEE, d. MMMM')}
      </Heading>

      {businessHours ? (
        <SlotSelect
          selectedDate={selectedDate}
          onSelect={handleSlotSelect}
          preBookings={validPreBookings}
          businessHours={businessHours}
        />
      ) : (
        <>Loading...</>
      )}
    </Stack>
  );
}
